// Software Licences -- list, server-side totals, filters, Add.
// Shared by /admin, /manager and /technician (unit scope is enforced by the
// backend; the Unit filter is only offered when the caller can see more
// than one unit).
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaKey, FaPlus, FaSyncAlt, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaChair } from "react-icons/fa";
import api from "../../api";
import { usePermissions } from "../../context/PermissionContext";
import {
  PageHeader, StatCardGrid, StatCard, Button, SearchInput, FilterSelect, FilterToolbar,
  DataTable, Pagination, ErrorState,
} from "../../components/ui";
import LicenseFormModal from "./LicenseFormModal";
import {
  LICENSE_TYPES, STATUS_OPTIONS, VIEW_OPTIONS, LicenseStatusBadge, typeLabel, money, fmtDate,
  licenceBasePath, apiError,
} from "./licenseFormat";
import "./SoftwareLicenses.css";

const PAGE_SIZE = 20;

const COLUMNS = [
  { key: "software", label: "Software", minWidth: 180 },
  { key: "vendor", label: "Vendor" },
  { key: "unit", label: "Unit" },
  { key: "type", label: "Licence Type" },
  { key: "total", label: "Total Seats", align: "center" },
  { key: "assigned", label: "Assigned", align: "center" },
  { key: "available", label: "Available", align: "center" },
  { key: "amount", label: "Purchase Amount" },
  { key: "start", label: "Start Date" },
  { key: "expiry", label: "Expiry Date" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions", align: "center" },
];

export default function SoftwareLicensesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = licenceBasePath(location.pathname);
  const { can } = usePermissions();
  const canCreate = can("software_licenses", "create");

  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ unit_id: "", vendor: "", license_type: "", status: "", view: "" });
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, filters]);

  useEffect(() => {
    api.get("/software-licenses/meta").then((res) => setMeta(res.data?.data || null)).catch(() => setMeta(null));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = { page, limit: PAGE_SIZE, q: debouncedSearch || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    Promise.all([
      api.get("/software-licenses", { params }),
      // Totals: whole scope (optionally one unit), never this page.
      api.get("/software-licenses/summary", { params: filters.unit_id ? { unit_id: filters.unit_id } : {} }),
    ])
      .then(([listRes, sumRes]) => {
        setRows(listRes.data?.data || []);
        setTotal(listRes.data?.pagination?.total || 0);
        setSummary(sumRes.data?.data || null);
      })
      .catch((err) => setError(apiError(err, "Failed to load software licences.")))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const multiUnit = !!meta?.can_choose_unit;

  return (
    <div className="sl-page">
      <PageHeader
        eyebrow="Assets"
        title="Software Licences"
        description="Software purchased for each unit, who or what each licence seat is assigned to, and its expiry and value."
        actions={
          <>
            <Button variant="secondary" icon={<FaSyncAlt />} onClick={load} disabled={loading}>Refresh</Button>
            {canCreate && <Button icon={<FaPlus />} onClick={() => setShowForm(true)}>Add Software Licence</Button>}
          </>
        }
      />

      <StatCardGrid>
        <StatCard icon={<FaKey />} label="Total Licences" value={summary?.total_licences ?? 0} loading={!summary} />
        <StatCard icon={<FaCheckCircle />} tone="success" label="Active Licences" value={summary?.active_licences ?? 0} loading={!summary} />
        <StatCard icon={<FaExclamationTriangle />} tone="warning" label={`Expiring in ${summary?.expiring_soon_days ?? 30} days`} value={summary?.expiring_soon ?? 0} loading={!summary} />
        <StatCard icon={<FaTimesCircle />} tone="danger" label="Expired Licences" value={summary?.expired_licences ?? 0} loading={!summary} />
      </StatCardGrid>

      {summary && (
        <div className="sl-summary-strip">
          <div><span>Seats (active licences)</span><strong><FaChair aria-hidden="true" /> {summary.assigned_seats} / {summary.total_seats} assigned · {summary.available_seats} available</strong></div>
          <div><span>Total Purchased Licence Value</span><strong>{money(summary.total_purchased_value)}</strong></div>
          <div><span>Active Licence Value</span><strong className="sl-good">{money(summary.active_value)}</strong></div>
          <div><span>Expired Licence Value</span><strong className="sl-bad">{money(summary.expired_value)}</strong></div>
        </div>
      )}

      {/* `.sl-filter-box` is the size container: the toolbar's columns follow the
          width actually left after the sidebar, not the viewport. */}
      <div className="sl-filter-box">
        <FilterToolbar className={`sl-filters ${multiUnit ? "sl-filters--n5" : "sl-filters--n4"}`}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search software, vendor, invoice…" aria-label="Search software, vendor, invoice" />
          {multiUnit && (
            <FilterSelect
              value={filters.unit_id}
              onChange={setFilter("unit_id")}
              aria-label="Filter by unit"
              allLabel="All Units"
              options={(meta?.units || []).map((u) => ({ value: String(u.id), label: u.name }))}
            />
          )}
          <FilterSelect value={filters.vendor} onChange={setFilter("vendor")} aria-label="Filter by vendor" allLabel="All Vendors" options={(meta?.vendors || []).map((v) => ({ value: v, label: v }))} />
          <FilterSelect value={filters.license_type} onChange={setFilter("license_type")} aria-label="Filter by licence type" allLabel="All Types" options={LICENSE_TYPES} />
          <FilterSelect value={filters.status} onChange={setFilter("status")} aria-label="Filter by status" allLabel="Status (excl. archived)" options={STATUS_OPTIONS} />
          {/* Quick view (filters.view): Active / Expiring Soon / Expired /
              Assigned / Seats Available -- same values as before, now labelled. */}
          <FilterSelect value={filters.view} onChange={setFilter("view")} aria-label="Quick view" allLabel="View: All Licences" options={VIEW_OPTIONS} />
        </FilterToolbar>
      </div>

      {error ? (
        <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />
      ) : (
        <DataTable
          columns={COLUMNS}
          loading={loading}
          empty={rows.length === 0}
          emptyTitle="No software licences"
          emptyMessage="No licence matches these filters."
        >
          {rows.map((l) => (
            <tr key={l.id}>
              <td>
                <button type="button" className="sl-link" onClick={() => navigate(`${basePath}/${l.id}`)}>{l.software_name}</button>
                {l.purchase_reference && <small className="d-block text-muted">Ref: {l.purchase_reference}</small>}
              </td>
              <td>{l.vendor_name || "-"}</td>
              <td>{l.unit_name || "-"}</td>
              <td>{typeLabel(l.license_type)}</td>
              <td className="ui-td-center">{l.total_seats}</td>
              <td className="ui-td-center">{l.assigned_seats}</td>
              <td className="ui-td-center">{l.available_seats}</td>
              <td>{money(l.purchase_amount)}</td>
              <td>{fmtDate(l.start_date)}</td>
              <td>{l.license_type === "PERPETUAL" && !l.expiry_date ? "Perpetual" : fmtDate(l.expiry_date)}</td>
              <td><LicenseStatusBadge licence={l} /></td>
              <td className="ui-td-center">
                <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/${l.id}`)}>View</Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        totalItems={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <LicenseFormModal
        show={showForm}
        meta={meta}
        onClose={() => setShowForm(false)}
        onSaved={(saved) => {
          setShowForm(false);
          if (saved?.id) navigate(`${basePath}/${saved.id}`);
          else load();
        }}
      />
    </div>
  );
}
